/**
 * Helpers for the auth step that runs before the app renders (APP_INITIALIZER).
 * If that step throws, Angular never renders anything, so failures here have to be
 * retried or shown to the user directly in the page.
 */

export const AUTH_INIT_MAX_ATTEMPTS = 3;
export const AUTH_INIT_RETRY_DELAY_MS = 1000;
export const AUTH_INIT_ERROR_ELEMENT_ID = "auth-init-error";

/**
 * A 400/401 from the token endpoint means the login code or token is no longer good
 * (for example, the code was already used). Retrying won't help; the user needs a fresh login.
 */
export function isUnrecoverableAuthError(err: unknown): boolean {
  const status = (err as { status?: number } | null)?.status;
  return status === 400 || status === 401;
}

/** Waits a little longer after each failed attempt: 1s, 2s, ... */
export function getRetryDelayMs(attempt: number, baseDelayMs = AUTH_INIT_RETRY_DELAY_MS): number {
  return baseDelayMs * attempt;
}

/**
 * Builds the URL to reload when the user clicks "Try again". Drops the one-time `code`
 * and `state` login params so the reload doesn't reuse a code that may already be spent,
 * and sends the user back to the page they were originally going to (stored in `state`).
 */
export function getRetryUrl(pathname: string, search: string): string {
  const params = new URLSearchParams(search);
  const state = params.get("state");

  if (params.has("code") && state) {
    try {
      const target = decodeURIComponent(state);
      if (target.startsWith("/") && !target.startsWith("//")) {
        return target;
      }
    } catch {
      // Bad state value; fall through and just strip the params.
    }
  }

  params.delete("code");
  params.delete("state");
  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
}

export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Runs `attemptFn` up to `maxAttempts` times. Stops early on errors that retrying
 * can't fix. Throws the last error if every attempt fails.
 */
export async function runWithRetry<T>(
  attemptFn: () => Promise<T>,
  options: {
    maxAttempts?: number;
    wait?: (ms: number) => Promise<void>;
    shouldRetry?: (err: unknown) => boolean;
  } = {}
): Promise<T> {
  const maxAttempts = options.maxAttempts ?? AUTH_INIT_MAX_ATTEMPTS;
  const wait = options.wait ?? delay;
  const shouldRetry = options.shouldRetry ?? ((err) => !isUnrecoverableAuthError(err));

  let lastError: unknown;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await attemptFn();
    } catch (err) {
      lastError = err;
      if (attempt === maxAttempts || !shouldRetry(err)) {
        break;
      }
      console.warn(`Sign-in attempt ${attempt} failed, retrying`, err);
      await wait(getRetryDelayMs(attempt));
    }
  }
  throw lastError;
}

/**
 * Shows a plain error message with a "Try again" button. Built with raw DOM because
 * Angular hasn't rendered at this point and won't if the initializer fails.
 */
export function showAuthInitError(doc: Document, onRetry: () => void): void {
  if (doc.getElementById(AUTH_INIT_ERROR_ELEMENT_ID)) {
    return;
  }

  const container = doc.createElement("div");
  container.id = AUTH_INIT_ERROR_ELEMENT_ID;
  container.setAttribute("role", "alert");
  container.style.cssText =
    "display:flex;flex-direction:column;align-items:center;justify-content:center;" +
    "gap:16px;min-height:100vh;padding:24px;box-sizing:border-box;text-align:center;" +
    "font-family:Roboto,'Helvetica Neue',sans-serif;color:#1d1d1d;";

  const heading = doc.createElement("h1");
  heading.textContent = "We couldn't sign you in";
  heading.style.cssText = "margin:0;font-size:24px;font-weight:500;";

  const message = doc.createElement("p");
  message.textContent =
    "Something went wrong while connecting to me@slalom. Check your network or VPN connection and try again.";
  message.style.cssText = "margin:0;max-width:480px;font-size:16px;line-height:1.5;";

  const button = doc.createElement("button");
  button.type = "button";
  button.textContent = "Try again";
  button.style.cssText =
    "padding:10px 24px;font-size:16px;border:none;border-radius:4px;cursor:pointer;" +
    "background:#0c62fb;color:#fff;";
  button.addEventListener("click", onRetry);

  container.append(heading, message, button);
  doc.body.appendChild(container);
}
